import { useAppTranslation } from '../i18n/useAppTranslation';
import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Button, Box,
} from '@mui/material';
import { WarningAmber } from '@mui/icons-material';

/**
 * Confirmation avant action irreversible.
 *
 * Remplace window.confirm(), dont le rendu natif ignore le theme et dont le
 * bouton « OK » ne dit jamais ce qui va reellement se passer. Ici, le libelle
 * decrit l'action ("Supprimer la plage") et le bouton destructif est rouge.
 *
 * Usage :
 *   const confirm = useConfirm();
 *   if (await confirm({ title: 'Supprimer ?', confirmLabel: 'Supprimer' })) { ... }
 */

interface ConfirmOptions {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
}

type Resolveur = (valeur: boolean) => void;

const ConfirmContext = createContext<((o: ConfirmOptions) => Promise<boolean>) | undefined>(undefined);

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { tr } = useAppTranslation();
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolveur = useRef<Resolveur | null>(null);

  const confirm = useCallback((o: ConfirmOptions) => {
    setOptions(o);
    return new Promise<boolean>((resolve) => {
      resolveur.current = resolve;
    });
  }, []);

  const repondre = (valeur: boolean) => {
    resolveur.current?.(valeur);
    resolveur.current = null;
    setOptions(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog
        open={options !== null}
        onClose={() => repondre(false)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="confirm-dialog-title"
      >
        <DialogTitle id="confirm-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {options?.destructive !== false && <WarningAmber color="warning" />}
          <Box component="span" sx={{ fontWeight: 800 }}>{tr(options?.title || '')}</Box>
        </DialogTitle>
        {options?.description && (
          <DialogContent>
            <DialogContentText>{tr(options.description)}</DialogContentText>
          </DialogContent>
        )}
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => repondre(false)} color="inherit" sx={{ fontWeight: 700 }}>
            {tr(options?.cancelLabel || tr("Annuler"))}
          </Button>
          <Button
            onClick={() => repondre(true)}
            variant="contained"
            color={options?.destructive === false ? 'primary' : 'error'}
            autoFocus
            sx={{ fontWeight: 800 }}
          >
            {tr(options?.confirmLabel || tr("Confirmer"))}
          </Button>
        </DialogActions>
      </Dialog>
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (ctx === undefined) {
    throw new Error('useConfirm doit être utilisé à l\'intérieur d\'un ConfirmProvider');
  }
  return ctx;
};
